// The sign-in gate, and the one place that knows Clerk exists.
//
// Everything downstream reads this context instead of Clerk's hooks, for a reason that is not
// tidiness: `DASHBOARD_AUTH_DISABLED=true` is the documented local-development mode, and in it
// there is no ClerkProvider at all. Clerk's `useAuth` throws outside its provider, so a
// component reaching for it directly would work signed-in and crash locally. The two modes
// implement the same small interface and nothing below has to know which one it got.
//
// This is also the real boundary's *client* half only. The server verifies the token on every
// /api/* handler and will 401 regardless of what this file decides to render.

import {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState,
} from 'react';
import type { ReactNode } from 'react';
import { ClerkProvider, SignIn, useAuth, useUser } from '@clerk/clerk-react';
import { fetchAuthConfig, isEmbedPath } from '../api/client';
import type { AuthConfig } from '../api/types';
import type { TokenGetter } from '../api/client';
import { isFramed, postToExtension } from '../extension/bridge.ts';
import {
  FrameTokenSource, SIGN_IN_POLL_ATTEMPTS, emailFromToken, firstTokenWithRetries, pollForToken,
} from '../extension/frameToken.ts';
import { Gate } from './Gate';

// Signed out inside the extension's side panel. One link out, a manual re-check, and a status
// for the panel. The frame keeps asking on its own meanwhile (see EmbedExtensionAuth).
function EmbedSignedOut({ onCheckAgain }: { onCheckAgain: () => void }) {
  useEffect(() => { postToExtension({ type: 'scout:embed-status', state: 'signed_out' }); }, []);
  return (
    <div className="embedgate">
      <p className="eyebrow">Scout</p>
      <p>Sign in on the outbound dashboard in this Chrome profile. This panel picks it up within a few seconds once you have.</p>
      <a className="btn primary" href={window.location.origin + '/'} target="_blank" rel="noopener">
        Open the dashboard to sign in
      </a>
      <button type="button" className="btn" onClick={onCheckAgain}>I have signed in: check again</button>
    </div>
  );
}

function waitMilliseconds(milliseconds: number): Promise<void> {
  return new Promise(resolve => { setTimeout(resolve, milliseconds); });
}

// Inside the side panel with the frame's own Clerk signed out: ask the extension for its token
// (`extension/frameToken.ts`). The token stays in the source's memory and is never logged.
//
// Until the answer is known the panel shows a neutral "Loading Scout…", never the sign-in card:
// an empty first answer is asked again (`firstTokenWithRetries`), and the shell is told the frame
// is still checking, so its 15 s "did not load" fallback does not cover a slow worker.
function EmbedExtensionAuth({ children }: { children: ReactNode }) {
  const source = useMemo(() => (isFramed() ? new FrameTokenSource(window) : null), []);
  const [state, setState] = useState<{ checked: boolean; email: string | null; token: boolean }>(
    { checked: false, email: null, token: false },
  );

  useEffect(() => {
    if (!source) {
      setState({ checked: true, email: null, token: false });
      return;
    }
    let cancelled = false;
    postToExtension({ type: 'scout:embed-status', state: 'checking' });
    void firstTokenWithRetries(source.getToken, waitMilliseconds).then(token => {
      if (cancelled) return;
      const has_token = Boolean(token);
      console.info(`framed dashboard asked the extension for a session token. has_token=${has_token}`);
      setState({ checked: true, email: token ? emailFromToken(token) : null, token: has_token });
    });
    return () => { cancelled = true; };
  }, [source]);

  // A token that arrives after "signed out" (the strategist signed in on a dashboard tab) signs
  // the frame in where it stands: no reload, no click on the extension.
  const signedOut = state.checked && !state.token;
  const acceptLateToken = useCallback((token: string | null, trigger: string) => {
    const has_token = Boolean(token);
    console.info(`framed dashboard asked the extension again while signed out. has_token=${has_token} trigger=${trigger}`);
    if (!token) return;
    setState({ checked: true, email: emailFromToken(token), token: true });
  }, []);
  const checkAgain = useCallback((trigger: string) => {
    if (!source) return;
    void source.getToken({ skipCache: true }).then(token => acceptLateToken(token, trigger));
  }, [source, acceptLateToken]);

  // While signed out: ask every few seconds for a while (`pollForToken`), and again whenever the
  // panel gets focus or becomes visible, which is what coming back from the dashboard tab does.
  useEffect(() => {
    if (!source || !signedOut) return;
    let cancelled = false;
    const isCancelled = () => cancelled;
    void pollForToken(source.getToken, waitMilliseconds, isCancelled).then(token => {
      if (cancelled) return;
      if (token) {
        acceptLateToken(token, 'poll');
        return;
      }
      const attempts = SIGN_IN_POLL_ATTEMPTS;
      console.info(`framed dashboard stopped polling the extension for a session token; focus or Check again still ask. attempts=${attempts}`);
    });
    const onFocus = () => checkAgain('focus');
    const onVisibility = () => { if (document.visibilityState === 'visible') checkAgain('visible'); };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      cancelled = true;
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [source, signedOut, acceptLateToken, checkAgain]);

  const email = state.email;
  const value = useMemo<AuthValue | null>(
    () => (source ? { enabled: true, email, getToken: source.getToken, signOut: () => window.location.reload() } : null),
    [source, email],
  );

  if (!state.checked) return <div className="embedgate" aria-busy="true"><p>Loading Scout…</p></div>;
  if (!state.token || !value) return <EmbedSignedOut onCheckAgain={() => checkAgain('button')} />;
  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

interface AuthValue {
  enabled: boolean;
  email: string | null;
  getToken: TokenGetter;
  signOut: () => void;
}

const AuthCtx = createContext<AuthValue | null>(null);

export function useAuthContext(): AuthValue {
  const value = useContext(AuthCtx);
  if (!value) throw new Error('useAuthContext used outside AuthProvider');
  return value;
}

// A token getter that never returns one. Local development has nobody to sign in, so requests
// go out unauthenticated and auth.py's placeholder caller answers them.
const noToken: TokenGetter = async () => null;

// Nothing in the disabled mode ever changes, so it is one object for the life of the page
// rather than a new one per render.
const disabledValue: AuthValue = {
  enabled: false,
  email: null,
  getToken: noToken,
  signOut: () => {},
};

// Bridges Clerk's hooks into the shared shape, and holds the gate closed until Clerk has
// actually loaded -- otherwise the sign-in form flashes for a signed-in user on every reload.
//
// Everything below is about *identity*, not tidiness. Clerk refreshes its session token on a
// timer and re-renders this component when it does. A context value rebuilt on each of those
// renders is a new `getToken` every few minutes, and any `useEffect` depending on it re-runs --
// which is how a chosen sequence in the push picker used to vanish mid-selection. The wrappers
// handed out here therefore keep one identity for the life of the component.
function ClerkBridge({ children }: { children: ReactNode }) {
  const { isLoaded, isSignedIn, getToken, signOut } = useAuth();
  const { user } = useUser();

  // Clerk's own `getToken`/`signOut` are free to change identity on each refresh, so the current
  // pair lives in a ref and the stable wrappers call through to whatever is in it now.
  const clerk = useRef({ getToken, signOut });
  useEffect(() => { clerk.current = { getToken, signOut }; }, [getToken, signOut]);

  const stableGetToken = useCallback<TokenGetter>(options => clerk.current.getToken(options), []);
  const stableSignOut = useCallback(() => {
    void clerk.current.signOut().then(() => window.location.reload());
  }, []);

  const email = user?.primaryEmailAddress?.emailAddress ?? null;

  // The email is the only thing consumers can actually observe changing, so it is the only
  // reason to hand them a new value.
  const value = useMemo<AuthValue>(
    () => ({ enabled: true, email, getToken: stableGetToken, signOut: stableSignOut }),
    [email, stableGetToken, stableSignOut],
  );

  if (!isLoaded) return <Gate message="Loading…" />;

  // The extension's framed panel cannot sign in itself: Google refuses to render in a frame,
  // and the dev instance's Clerk state is partitioned there. It borrows the extension's token,
  // and only says "signed out" when the extension has none either (W8).
  if (!isSignedIn && isEmbedPath(window.location.pathname)) {
    return <EmbedExtensionAuth>{children}</EmbedExtensionAuth>;
  }

  if (!isSignedIn) {
    return (
      <Gate message="Sign in with your Cartesia account to continue.">
        <SignIn routing="virtual" />
      </Gate>
    );
  }

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [config, setConfig] = useState<AuthConfig | null>(null);
  const [configError, setConfigError] = useState<string | null>(null);

  useEffect(() => {
    fetchAuthConfig().then(setConfig).catch((error: Error) => setConfigError(error.message));
  }, []);

  // A forgotten environment variable on Render should be legible rather than silent. The old
  // page said the same thing in the same place.
  if (configError) {
    return (
      <Gate>
        <div className="err">
          <b>Cannot start sign-in.</b>
          <br />
          {configError}
        </div>
      </Gate>
    );
  }

  if (!config) return <Gate message="Loading…" />;

  if (!config.enabled) {
    return <AuthCtx.Provider value={__MOCK_API__ && config.preview_email ? { ...disabledValue, email: config.preview_email } : disabledValue}>{children}</AuthCtx.Provider>;
  }

  return (
    <ClerkProvider publishableKey={config.publishable_key ?? ''}>
      <ClerkBridge>{children}</ClerkBridge>
    </ClerkProvider>
  );
}
